/**
 * SettingsHome.jsx — Account settings sections inside the hamburger menu.
 *
 * Hosts what used to live in the floating "Settings" widget (issue #267):
 *   - Profile  (ESO @-tag, embed colour, alerts, allow-others)
 *   - Privacy  (5 privacy flags)
 *   - API Keys (mint/revoke; one-time reveal)
 *   - Memories (Fissal facts; delete-one + forget-all)
 *
 * Lazy: only fetches when the host signal is true (i.e. the parent's
 * Settings tab is open). Reuses `.v2-settings__*` styles already loaded
 * via web/v2/src/widgets/index.jsx — no new design tokens.
 *
 * CSP: zero inline event handlers; Solid compiles to addEventListener.
 */
import { createSignal, createResource, Show, For } from 'solid-js';
import { EMOJI } from '../lib/emoji.js';
import { toast } from '../lib/toastStore.js';
import ConfirmDialog from './ConfirmDialog.jsx';
import ColorSwatch from './ColorSwatch.jsx';

async function postJSON(url, body, method = 'POST') {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'content-type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let json = null;
  try { json = await res.json(); } catch { /* ignore */ }
  if (!res.ok) {
    const err = new Error((json && json.error) || `HTTP ${res.status}`);
    err.status = res.status;
    err.json = json;
    throw err;
  }
  return json || {};
}

function fmtDate(ts) {
  if (!ts) return '—';
  try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

/**
 * SettingsSection — animated collapsible section.
 *
 * Open/close state persists under `v2:settings:open:<id>` in
 * localStorage so sections re-open where the user left them.
 *
 * Props: id, icon, title, defaultOpen (bool, default false), children.
 */
function SettingsSection(props) {
  function stored() {
    try {
      const v = localStorage.getItem(`v2:settings:open:${props.id}`);
      if (v === null) return props.defaultOpen ?? false;
      return v !== 'false';
    } catch { return props.defaultOpen ?? false; }
  }

  const [open, setOpen] = createSignal(stored());

  function toggle() {
    const next = !open();
    setOpen(next);
    try {
      localStorage.setItem(`v2:settings:open:${props.id}`, String(next));
    } catch { /* storage unavailable — state still works, just forgets on reload */ }
  }

  return (
    <div class="v2-stg-section" classList={{ [`v2-stg-section--${props.id}`]: !!props.id }}>
      <button
        type="button"
        class="v2-stg-section__hdr"
        classList={{ 'is-open': open() }}
        onClick={toggle}
        aria-expanded={String(open())}
      >
        <span class="v2-stg-section__icon" aria-hidden="true">{props.icon}</span>
        <span class="v2-stg-section__title">{props.title}</span>
        <span class="v2-stg-section__chevron" aria-hidden="true">›</span>
      </button>
      <div class="v2-stg-section__body" classList={{ 'is-open': open() }}>
        <div class="v2-stg-section__inner">
          {props.children}
        </div>
      </div>
    </div>
  );
}

/**
 * Props:
 *   active() — accessor: only fetch while truthy (parent tab visible).
 *   loggedIn — accessor: viewer is authenticated.
 */
export default function SettingsHome(props) {
  const [busy, setBusy] = createSignal(false);

  const [data, { refetch }] = createResource(
    () => (props.active() && props.loggedIn()) ? 'load' : null,
    async () => {
      try {
        const r = await fetch('/api/v2/widgets/settings', { credentials: 'same-origin' });
        if (!r.ok) return null;
        const j = await r.json();
        return j?.payload || null;
      } catch { return null; }
    },
  );

  async function saveSettings(patch) {
    setBusy(true);
    try {
      await postJSON('/api/me/settings', patch);
      await refetch();
      toast.success('Saved.');
    } catch (e) {
      toast.error('Save failed', { detail: e.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Show when={props.loggedIn()} fallback={
      <p class="v2-home__hint v2-home__hint--small">
        {EMOJI.warn} Sign in with Discord to manage profile, privacy, API keys
        and memory.
      </p>
    }>
      <Show when={data()} fallback={<p class="v2-home__hint v2-home__hint--small">Loading account settings…</p>}>
        <div class="v2-settings v2-home__account-settings">
          <SettingsSection id="profile" icon={EMOJI.user || '👤'} title="Profile" defaultOpen>
            <ProfileSection
              profile={data().profile || {}}
              busy={busy()}
              onSave={saveSettings}
            />
          </SettingsSection>

          <SettingsSection id="privacy" icon={EMOJI.lock || '🔒'} title="Privacy">
            <PrivacySection
              privacy={data().privacy || {}}
              busy={busy()}
              onSave={saveSettings}
            />
          </SettingsSection>

          <SettingsSection id="apikeys" icon={EMOJI.key || '🔑'} title="API Keys">
            <ApiKeysSection
              keys={data().apiKeys || []}
              limit={data().apiKeyLimit || 5}
              busy={busy()}
              setBusy={setBusy}
              refetch={refetch}
            />
          </SettingsSection>

          <SettingsSection id="memories" icon={EMOJI.ai || '🤖'} title="Fissal Memories">
            <MemoriesSection
              memories={data().memories || []}
              busy={busy()}
              setBusy={setBusy}
              refetch={refetch}
            />
          </SettingsSection>
        </div>
      </Show>
    </Show>
  );
}

// ── Profile ────────────────────────────────────────────────────────────
function ProfileSection(props) {
  const [tag, setTag] = createSignal(props.profile.atTag || '');
  const [color, setColor] = createSignal(props.profile.color || '#FF7433');
  const [alertMe, setAlertMe] = createSignal(!!props.profile.active);
  const [allowOthers, setAllowOthers] = createSignal(props.profile.allowOthers !== false);

  function submit(ev) {
    ev.preventDefault();
    props.onSave({
      esoTag: tag().trim(),
      color: color(),
      alertMe: alertMe(),
      allowOthers: allowOthers(),
    });
  }

  return (
    <form class="v2-settings__form" onSubmit={submit}>
      <label class="v2-settings__field">
        <span class="v2-settings__label">ESO @-Tag</span>
        <input
          type="text"
          class="form-input"
          value={tag()}
          placeholder="@YourESOTag"
          maxLength={64}
          onInput={(e) => setTag(e.currentTarget.value)}
        />
        <span class="v2-settings__hint">
          {props.profile.tagInDatabase
            ? `${EMOJI.ok} Tag verified in the sales database.`
            : props.profile.atTag
              ? `${EMOJI.info} Tag set — will verify after the next sync.`
              : 'Your Elder Scrolls Online @-tag, not your Discord username.'}
        </span>
      </label>

      <label class="v2-settings__field">
        <span class="v2-settings__label">Embed colour</span>
        <ColorSwatch value={color()} onInput={(e) => setColor(e.currentTarget.value)} />
      </label>

      <label class="v2-settings__toggle-row">
        <input
          type="checkbox"
          checked={alertMe()}
          onChange={(e) => setAlertMe(e.currentTarget.checked)}
        />
        <span>{EMOJI.alert} Alert me — DM when my items sell</span>
      </label>

      <label class="v2-settings__toggle-row">
        <input
          type="checkbox"
          checked={allowOthers()}
          onChange={(e) => setAllowOthers(e.currentTarget.checked)}
        />
        <span>{EMOJI.user} Allow others to look up my @-tag</span>
      </label>

      <div class="v2-settings__actions">
        <button type="submit" class="btn btn-primary" disabled={props.busy}>
          {props.busy ? '…' : 'Save profile'}
        </button>
      </div>
    </form>
  );
}

// ── Privacy ────────────────────────────────────────────────────────────
function PrivacySection(props) {
  const FIELDS = [
    { key: 'anonymous',           label: 'Anonymise — show as "Anon. Redfur Trader" on the leaderboard' },
    { key: 'hideFromLeaderboard', label: 'Hide me from the public leaderboard entirely' },
    { key: 'hidePointsTotal',     label: 'Hide my point totals from public sync stats' },
    { key: 'hideSaleHistory',     label: 'Hide my sale history from other users' },
    { key: 'hideAvatar',          label: 'Hide my Discord avatar across the dashboard' },
  ];

  function toggle(key) {
    const next = !props.privacy[key];
    props.onSave({ [key]: next });
  }

  return (
    <div class="v2-settings__form">
      <p class="v2-settings__hint">
        {EMOJI.info} Each toggle saves immediately. Privacy filters apply to
        every public widget — leaderboards, sync stats, sale feeds and
        guild detail panels honour them in real time.
      </p>
      <For each={FIELDS}>{(f) => (
        <label class="v2-settings__toggle-row">
          <input
            type="checkbox"
            checked={!!props.privacy[f.key]}
            disabled={props.busy}
            onChange={() => toggle(f.key)}
          />
          <span>{f.label}</span>
        </label>
      )}</For>
    </div>
  );
}

// ── API Keys ───────────────────────────────────────────────────────────
function ApiKeysSection(props) {
  const [label, setLabel] = createSignal('');
  const [revealed, setRevealed] = createSignal(null);
  const [confirmState, setConfirmState] = createSignal(null);

  async function createKey(ev) {
    ev.preventDefault();
    if (props.busy) return;
    props.setBusy(true);
    try {
      const r = await postJSON('/api/me/api-keys', { label: label().slice(0, 64) });
      setRevealed({ key: r.key, prefix: r.prefix, label: r.label });
      setLabel('');
      await props.refetch();
      toast.success('Key created — copy it now, it is not shown again.');
    } catch (e) {
      toast.error('Could not create key', { detail: e.message });
    } finally {
      props.setBusy(false);
    }
  }

  function revoke(id) {
    if (props.busy) return;
    setConfirmState({
      title: 'Revoke API key?',
      body: 'Existing scripts or services using this key will stop working immediately.',
      confirmLabel: 'Revoke',
      confirmKind: 'danger',
      action: async () => {
        props.setBusy(true);
        try {
          await postJSON('/api/me/api-keys/' + id, null, 'DELETE');
          await props.refetch();
          toast.success('Key revoked.');
        } catch (e) {
          toast.error('Revoke failed', { detail: e.message });
        } finally {
          props.setBusy(false);
        }
      },
    });
  }

  async function copyKey() {
    const k = revealed()?.key;
    if (!k) return;
    try {
      await navigator.clipboard.writeText(k);
      toast.success('Copied to clipboard.');
    } catch {
      toast.error('Clipboard blocked — copy manually.');
    }
  }

  return (
    <div class="v2-settings__form">
      <p class="v2-settings__hint">
        {EMOJI.info} Personal API keys authenticate scripts via the
        <code> X-API-Key</code> header. Maximum {props.limit} active keys.
      </p>

      <Show when={revealed()}>
        <div class="v2-settings__reveal" role="alert">
          <div class="v2-settings__reveal-title">{EMOJI.warn} New key (shown once)</div>
          <div class="v2-settings__reveal-key">
            <code>{revealed().key}</code>
            <button type="button" class="btn" onClick={copyKey}>Copy</button>
            <button type="button" class="btn" onClick={() => setRevealed(null)}>Dismiss</button>
          </div>
        </div>
      </Show>

      <form class="v2-settings__row" onSubmit={createKey}>
        <input
          type="text"
          class="form-input"
          placeholder="Key label (e.g. ‘home script’)"
          maxLength={64}
          value={label()}
          onInput={(e) => setLabel(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <button
          type="submit"
          class="btn btn-primary"
          disabled={props.busy || (props.keys.length >= props.limit)}
        >
          {EMOJI.add} New key
        </button>
      </form>

      <Show when={props.keys.length === 0}
        fallback={
          <table class="v2-settings__table">
            <thead>
              <tr>
                <th>Prefix</th>
                <th>Label</th>
                <th>Created</th>
                <th>Last used</th>
                <th>Calls</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <For each={props.keys}>{(k) => (
                <tr>
                  <td><code>{k.key_prefix}…</code></td>
                  <td>{k.label || <span class="v2-settings__dim">(unlabelled)</span>}</td>
                  <td>{fmtDate(k.created_at)}</td>
                  <td>{k.last_used_at ? fmtDate(k.last_used_at) : <span class="v2-settings__dim">never</span>}</td>
                  <td>{k.requests || 0}</td>
                  <td>
                    <button type="button" class="btn btn-danger btn-xs" disabled={props.busy} onClick={() => revoke(k.id)}>
                      Revoke
                    </button>
                  </td>
                </tr>
              )}</For>
            </tbody>
          </table>
        }
      >
        <div class="v2-empty">{EMOJI.info} No active API keys.</div>
      </Show>
      <Show when={confirmState()}>
        <ConfirmDialog
          open
          title={confirmState().title}
          body={confirmState().body}
          confirmLabel={confirmState().confirmLabel}
          confirmKind={confirmState().confirmKind}
          onConfirm={async () => {
            const a = confirmState().action;
            setConfirmState(null);
            await a?.();
          }}
          onCancel={() => setConfirmState(null)}
        />
      </Show>
    </div>
  );
}

// ── Memories ───────────────────────────────────────────────────────────
function MemoriesSection(props) {
  const [confirmState, setConfirmState] = createSignal(null);

  function deleteOne(m) {
    if (props.busy) return;
    const preview = (m.fact || '').slice(0, 80);
    setConfirmState({
      title: 'Forget this memory?',
      body: <>This permanently removes <code>{preview}{m.fact && m.fact.length > 80 ? '…' : ''}</code> from Fissal's memory.</>,
      confirmLabel: 'Forget',
      confirmKind: 'danger',
      action: async () => {
        props.setBusy(true);
        try {
          await postJSON('/api/me/memories/' + m.id, null, 'DELETE');
          await props.refetch();
          toast.success('Memory forgotten.');
        } catch (e) {
          toast.error('Delete failed', { detail: e.message });
        } finally {
          props.setBusy(false);
        }
      },
    });
  }

  function forgetAll() {
    if (props.busy) return;
    if (!props.memories.length) return;
    setConfirmState({
      title: `Forget all ${props.memories.length} memories?`,
      body: 'This permanently clears every fact Fissal has remembered about you. This cannot be undone.',
      confirmLabel: 'Forget all',
      confirmKind: 'danger',
      action: async () => {
        props.setBusy(true);
        try {
          await postJSON('/api/me/memories', null, 'DELETE');
          await props.refetch();
          toast.success('All memories cleared.');
        } catch (e) {
          toast.error('Forget-all failed', { detail: e.message });
        } finally {
          props.setBusy(false);
        }
      },
    });
  }

  return (
    <div class="v2-settings__form v2-settings__form--memories">
      <div class="v2-settings__row" style={{ 'align-items': 'center' }}>
        <p class="v2-settings__hint" style={{ flex: 1, margin: 0 }}>
          {EMOJI.info} Facts Fissal has remembered about you from chat. Delete
          any you'd like the AI to forget; “Forget all” wipes the record entirely.
        </p>
        <button
          type="button"
          class="btn btn-danger btn-xs"
          disabled={props.busy || props.memories.length === 0}
          onClick={forgetAll}
        >
          Forget all
        </button>
      </div>
      <Show when={props.memories.length} fallback={
        <div class="v2-empty">{EMOJI.info} No remembered facts yet.</div>
      }>
        <ul class="v2-settings__memlist v2-settings__memlist--user">
          <For each={props.memories}>{(m) => (
            <li class="v2-settings__memitem">
              <div class="v2-settings__memfact" title={m.fact}>{m.fact}</div>
              <div class="v2-settings__memmeta">
                <div class="v2-settings__memmeta-main">
                  <Show when={m.confidence != null}>
                    <span>conf: {Math.round((m.confidence || 0) * 100)}%</span>
                  </Show>
                  <span>{fmtDate((m.lastUpdated || m.timestamp) * 1000)}</span>
                </div>
                <button
                  type="button"
                  class="btn btn-xs btn-danger"
                  disabled={props.busy}
                  onClick={() => deleteOne(m)}
                >Forget</button>
              </div>
            </li>
          )}</For>
        </ul>
      </Show>
      <Show when={confirmState()}>
        <ConfirmDialog
          open
          title={confirmState().title}
          body={confirmState().body}
          confirmLabel={confirmState().confirmLabel}
          confirmKind={confirmState().confirmKind}
          onConfirm={async () => {
            const a = confirmState().action;
            setConfirmState(null);
            await a?.();
          }}
          onCancel={() => setConfirmState(null)}
        />
      </Show>
    </div>
  );
}
